import { useState } from 'react';
import {
  ShoppingCart, Trash2, Minus, Plus, ArrowLeft, CreditCard,
  AlertTriangle, CheckCircle, X, Info, LayoutGrid,
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { plvApi, getImageUrl } from '../api/plvApi';
import { DeliveryForm } from './DeliveryForm';
import type { CartItem, DeliveryInfo } from '../types/plv.types';
import { calculateTotalPriceForProduct, formatPrice } from '../utils/pricingUtils';
import { getNextQuantityTier, getPreviousQuantityTier } from '../utils/quantityUtils';
import { useAuthStore } from '../../../stores/auth-store';

interface CartProps {
  onBackToCatalog: () => void;
  onOrderCreated?: () => void;
}

export function Cart({ onBackToCatalog, onOrderCreated }: CartProps) {
  const { state, updateQuantity, removeItem, clearCart } = useCart();
  const { currentUser } = useAuthStore();
  const [itemToDelete, setItemToDelete] = useState<CartItem | null>(null);
  const [isOrdering, setIsOrdering] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [showOrderConfirmModal, setShowOrderConfirmModal] = useState(false);
  const [showDeliveryForm, setShowDeliveryForm] = useState(false);
  const [deliveryData, setDeliveryData] = useState<DeliveryInfo | null>(null);
  const [visualsValidated, setVisualsValidated] = useState(false);

  const defaultDeliveryData: DeliveryInfo = {
    contactLastname: '',
    contactFirstname: '',
    contactEmail: '',
    contactPhone: '',
    companyName: '',
    address: '',
    postalCode: '',
    city: '',
  };

  // Quantity tier changes
  const handleQuantityTierChange = (itemId: string, product: CartItem['product'], currentQuantity: number, direction: 'increase' | 'decrease') => {
    const newQuantity = direction === 'increase'
      ? getNextQuantityTier(product, currentQuantity)
      : getPreviousQuantityTier(product, currentQuantity);
    if (newQuantity <= 0) {
      const item = state.items.find(i => i.id === itemId);
      if (item) setItemToDelete(item);
    } else {
      updateQuantity(itemId, newQuantity);
    }
  };

  const confirmRemoval = () => {
    if (itemToDelete) removeItem(itemToDelete.id);
    setItemToDelete(null);
  };

  // Order creation
  const handleCreateOrder = async () => {
    if (state.items.length === 0 || !deliveryData || !currentUser || !currentUser.societyId) return;
    setIsOrdering(true);
    setOrderError(null);
    setShowOrderConfirmModal(false);

    try {
      await plvApi.createOrder({
        societyId: currentUser.societyId,
        orderedBy: currentUser.id,
        items: state.items.map(item => ({ productId: item.product.id, quantity: item.quantity })),
        emailNotification: true,
        deliveryContactLastname: deliveryData.contactLastname,
        deliveryContactFirstname: deliveryData.contactFirstname,
        deliveryContactEmail: deliveryData.contactEmail,
        deliveryContactPhone: deliveryData.contactPhone,
        deliveryCompanyName: deliveryData.companyName,
        deliveryAddress: deliveryData.address,
        deliveryZip: deliveryData.postalCode,
        deliveryCity: deliveryData.city,
      });

      setOrderSuccess(true);
      clearCart();
      setDeliveryData(null);
      onOrderCreated?.();
    } catch (error) {
      if (error instanceof Error) {
        setOrderError(error.message.includes('UUID') ? "Erreur de configuration : ID de société invalide." : error.message);
      } else {
        setOrderError('Une erreur inattendue est survenue.');
      }
    } finally {
      setIsOrdering(false);
    }
  };

  const resetOrderStates = () => {
    setOrderSuccess(false);
    setOrderError(null);
    setDeliveryData(null);
    setVisualsValidated(false);
  };

  const handleDeliveryConfirm = (data: DeliveryInfo) => {
    setDeliveryData(data);
    setShowDeliveryForm(false);
    setShowOrderConfirmModal(true);
  };

  // Empty cart
  if (state.items.length === 0 && !orderSuccess) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12 tw-text-gray-400">
        <ShoppingCart className="tw-w-16 tw-h-16 tw-mb-3" />
        <h4 className="tw-text-lg tw-font-medium tw-text-gray-500">Votre panier est vide</h4>
        <p className="tw-text-sm tw-mt-1 tw-mb-4">Ajoutez des produits depuis le catalogue pour commencer votre commande.</p>
        <button
          className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-bg-affinicar-secondary tw-text-white hover:tw-opacity-90 tw-transition-opacity"
          onClick={onBackToCatalog}
        >
          <LayoutGrid className="tw-w-4 tw-h-4" />
          Retour au catalogue
        </button>
      </div>
    );
  }

  const getItemImage = (item: CartItem) => {
    const img = item.product.files?.presentationImage || item.product.images?.[0];
    if (!img) return null;
    return img.startsWith('http') ? img : getImageUrl(img);
  };

  return (
    <>
      <div className="tw-space-y-4">
        {/* Header */}
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-items-start sm:tw-items-center tw-gap-3">
            <div>
              <h4 className="tw-text-lg tw-font-semibold tw-flex tw-items-center tw-gap-2">
                <ShoppingCart className="tw-w-5 tw-h-5 tw-text-affinicar-secondary" />
                Mon Panier
              </h4>
              <p className="tw-text-sm tw-text-gray-500 tw-mt-0.5">
                {state.totalItems} article{state.totalItems > 1 ? 's' : ''} dans le panier
              </p>
            </div>
            <button
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-border-red-300 tw-text-red-600 hover:tw-bg-red-50 tw-transition-colors"
              onClick={clearCart}
            >
              <Trash2 className="tw-w-4 tw-h-4" />
              Vider le panier
            </button>
          </div>
        </div>

        {/* Items */}
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-divide-y tw-divide-gray-100">
          {state.items.map(item => (
            <div key={item.id} className="tw-p-4">
              <div className="tw-flex tw-gap-4">
                {/* Image */}
                <div className="tw-w-20 tw-h-20 tw-flex-shrink-0 tw-bg-gray-50 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-overflow-hidden">
                  {getItemImage(item) ? (
                    <img src={getItemImage(item)!} alt={item.product.name} className="tw-max-h-full tw-max-w-full tw-object-contain" />
                  ) : (
                    <span className="tw-text-2xl">📦</span>
                  )}
                </div>

                {/* Details */}
                <div className="tw-flex-1 tw-min-w-0">
                  <h6 className="tw-text-sm tw-font-semibold tw-truncate">{item.product.name}</h6>
                  <span className="tw-text-xs tw-text-gray-500">{item.product.type}</span>

                  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-mt-2">
                    {/* Quantity controls */}
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <button
                        className="tw-p-1 tw-rounded tw-border tw-border-gray-300 tw-text-gray-500 hover:tw-bg-gray-100 disabled:tw-opacity-40 tw-transition-colors"
                        onClick={() => handleQuantityTierChange(item.id, item.product, item.quantity, 'decrease')}
                        disabled={getPreviousQuantityTier(item.product, item.quantity) === 0}
                      >
                        <Minus className="tw-w-3.5 tw-h-3.5" />
                      </button>
                      <span className="tw-font-bold tw-text-sm tw-min-w-[28px] tw-text-center">{item.quantity}</span>
                      <button
                        className="tw-p-1 tw-rounded tw-border tw-border-gray-300 tw-text-gray-500 hover:tw-bg-gray-100 disabled:tw-opacity-40 tw-transition-colors"
                        onClick={() => handleQuantityTierChange(item.id, item.product, item.quantity, 'increase')}
                        disabled={getNextQuantityTier(item.product, item.quantity) === item.quantity}
                      >
                        <Plus className="tw-w-3.5 tw-h-3.5" />
                      </button>
                    </div>

                    {/* Price */}
                    <div className="tw-text-sm">
                      <span className="tw-font-bold">{formatPrice(calculateTotalPriceForProduct(item.product, item.quantity))}</span>
                      <span className="tw-text-xs tw-text-gray-400 tw-ml-1">HT</span>
                    </div>

                    {/* Delete */}
                    <button
                      className="tw-ml-auto tw-p-1.5 tw-rounded tw-border tw-border-red-200 tw-text-red-500 hover:tw-bg-red-50 tw-transition-colors"
                      onClick={() => setItemToDelete(item)}
                    >
                      <Trash2 className="tw-w-4 tw-h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="tw-flex tw-justify-end">
          <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-w-full lg:tw-w-1/2">
            <div className="tw-px-4 tw-py-3 tw-border-b tw-border-gray-200">
              <h5 className="tw-text-sm tw-font-semibold">Récapitulatif</h5>
            </div>
            <div className="tw-p-4 tw-space-y-3">
              <div className="tw-flex tw-justify-between tw-items-center">
                <span className="tw-font-medium">Total HT :</span>
                <span className="tw-font-bold tw-text-lg tw-text-affinicar-secondary">{formatPrice(state.totalHT)}</span>
              </div>

              <button
                className="tw-w-full tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-2.5 tw-text-sm tw-font-medium tw-rounded-lg tw-bg-affinicar-secondary tw-text-white hover:tw-opacity-90 tw-transition-opacity disabled:tw-opacity-50"
                onClick={() => setShowDeliveryForm(true)}
                disabled={isOrdering || state.items.length === 0}
              >
                {isOrdering ? (
                  <>
                    <div className="tw-w-4 tw-h-4 tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full tw-animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <CreditCard className="tw-w-4 tw-h-4" />
                    Confirmer la commande
                  </>
                )}
              </button>

              {orderError && (
                <div className="tw-flex tw-items-start tw-gap-2 tw-p-3 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-text-sm tw-text-red-700">
                  <AlertTriangle className="tw-w-4 tw-h-4 tw-mt-0.5 tw-flex-shrink-0" />
                  <span className="tw-flex-1">{orderError}</span>
                  <button onClick={resetOrderStates} className="tw-text-red-400 hover:tw-text-red-600">
                    <X className="tw-w-4 tw-h-4" />
                  </button>
                </div>
              )}

              <button
                className="tw-w-full tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-border-gray-300 tw-text-gray-600 hover:tw-bg-gray-50 tw-transition-colors"
                onClick={onBackToCatalog}
              >
                <ArrowLeft className="tw-w-4 tw-h-4" />
                Continuer mes achats
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {itemToDelete && (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/50" onClick={() => setItemToDelete(null)}>
          <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-md tw-mx-4" onClick={e => e.stopPropagation()}>
            <div className="tw-px-6 tw-pt-5 tw-pb-2">
              <h5 className="tw-text-base tw-font-semibold tw-flex tw-items-center tw-gap-2">
                <AlertTriangle className="tw-w-5 tw-h-5 tw-text-amber-500" />
                Confirmer la suppression
              </h5>
            </div>
            <div className="tw-px-6 tw-py-4">
              <p className="tw-text-sm tw-mb-3">Êtes-vous sûr de vouloir retirer ce produit de votre panier ?</p>
              <div className="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                <div className="tw-w-12 tw-h-12 tw-flex-shrink-0 tw-bg-white tw-rounded tw-flex tw-items-center tw-justify-center tw-overflow-hidden">
                  {getItemImage(itemToDelete) ? (
                    <img src={getItemImage(itemToDelete)!} alt={itemToDelete.product.name} className="tw-max-h-full tw-max-w-full tw-object-contain" />
                  ) : (
                    <span className="tw-text-lg">📦</span>
                  )}
                </div>
                <div>
                  <h6 className="tw-text-sm tw-font-medium">{itemToDelete.product.name}</h6>
                  <span className="tw-text-xs tw-text-gray-500">
                    Quantité: {itemToDelete.quantity} — {formatPrice(calculateTotalPriceForProduct(itemToDelete.product, itemToDelete.quantity))} HT
                  </span>
                </div>
              </div>
            </div>
            <div className="tw-flex tw-justify-end tw-gap-2 tw-px-6 tw-pb-5">
              <button className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-border-gray-300 tw-text-gray-600 hover:tw-bg-gray-50" onClick={() => setItemToDelete(null)}>
                Annuler
              </button>
              <button className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-bg-red-600 tw-text-white hover:tw-bg-red-700" onClick={confirmRemoval}>
                <Trash2 className="tw-w-4 tw-h-4" />
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order confirmation modal */}
      {showOrderConfirmModal && (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/50" onClick={() => { setShowOrderConfirmModal(false); setVisualsValidated(false); }}>
          <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-2xl tw-mx-4 tw-max-h-[90vh] tw-overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-border-b tw-border-gray-200">
              <h5 className="tw-text-base tw-font-semibold tw-flex tw-items-center tw-gap-2">
                <ShoppingCart className="tw-w-5 tw-h-5 tw-text-affinicar-secondary" />
                Confirmer votre commande
              </h5>
              <button className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400" onClick={() => { setShowOrderConfirmModal(false); setVisualsValidated(false); }}>
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>
            <div className="tw-px-6 tw-py-4 tw-space-y-4">
              <p className="tw-text-sm">Vous êtes sur le point de passer commande pour les produits suivants :</p>

              <div className="tw-p-4 tw-bg-gray-50 tw-rounded-lg tw-space-y-2">
                {state.items.map(item => (
                  <div key={item.id} className="tw-flex tw-justify-between tw-items-center tw-text-sm">
                    <div>
                      <span className="tw-font-medium">{item.product.name}</span>
                      <span className="tw-text-gray-500 tw-ml-2">x{item.quantity}</span>
                    </div>
                    <span className="tw-font-bold">{formatPrice(calculateTotalPriceForProduct(item.product, item.quantity))}</span>
                  </div>
                ))}
                <div className="tw-border-t tw-border-gray-200 tw-pt-2 tw-mt-2 tw-flex tw-justify-between tw-items-center">
                  <span className="tw-font-semibold">Total HT :</span>
                  <span className="tw-font-bold tw-text-lg tw-text-affinicar-secondary">{formatPrice(state.totalHT)}</span>
                </div>
              </div>

              {/* Visuals warning */}
              <div className="tw-flex tw-items-start tw-gap-2 tw-p-3 tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-lg tw-text-sm tw-text-amber-700">
                <AlertTriangle className="tw-w-4 tw-h-4 tw-mt-0.5 tw-flex-shrink-0" />
                <div>
                  <strong>Attention :</strong> Veuillez vérifier les visuels de vos produits avant de valider. Les fichiers seront imprimés tels quels, sans modification ultérieure possible.
                </div>
              </div>

              {/* Visuals checkbox */}
              <label className="tw-flex tw-items-start tw-gap-3 tw-p-3 tw-bg-gray-50 tw-rounded-lg tw-cursor-pointer">
                <input
                  type="checkbox"
                  className="tw-mt-0.5 tw-rounded tw-border-gray-300 tw-text-affinicar-secondary focus:tw-ring-affinicar-secondary"
                  checked={visualsValidated}
                  onChange={e => setVisualsValidated(e.target.checked)}
                />
                <span className="tw-text-sm tw-font-medium">Je confirme avoir vérifié et validé tous les visuels de ma commande</span>
              </label>

              {/* Billing info */}
              <div className="tw-flex tw-items-start tw-gap-2 tw-p-3 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-text-sm tw-text-blue-700">
                <Info className="tw-w-4 tw-h-4 tw-mt-0.5 tw-flex-shrink-0" />
                <div>
                  Cette commande sera transmise automatiquement et vous recevrez une confirmation par email.<br />
                  <strong>Le montant sera intégré à votre facturation mensuelle Affinicar.</strong>
                </div>
              </div>
            </div>
            <div className="tw-flex tw-justify-end tw-gap-2 tw-px-6 tw-py-4 tw-border-t tw-border-gray-200">
              <button
                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-border-gray-300 tw-text-gray-600 hover:tw-bg-gray-50"
                onClick={() => { setShowOrderConfirmModal(false); setVisualsValidated(false); }}
                disabled={isOrdering}
              >
                Annuler
              </button>
              <button
                className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-bg-affinicar-secondary tw-text-white hover:tw-opacity-90 tw-transition-opacity disabled:tw-opacity-50"
                onClick={handleCreateOrder}
                disabled={isOrdering || !visualsValidated}
              >
                {isOrdering ? (
                  <>
                    <div className="tw-w-4 tw-h-4 tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full tw-animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <CreditCard className="tw-w-4 tw-h-4" />
                    Confirmer la commande
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order success modal */}
      {orderSuccess && (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/50">
          <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-md tw-mx-4">
            <div className="tw-px-6 tw-py-4 tw-bg-green-600 tw-text-white tw-rounded-t-xl">
              <h5 className="tw-text-base tw-font-semibold tw-flex tw-items-center tw-gap-2">
                <CheckCircle className="tw-w-5 tw-h-5" />
                Commande confirmée
              </h5>
            </div>
            <div className="tw-px-6 tw-py-6 tw-text-center">
              <CheckCircle className="tw-w-12 tw-h-12 tw-text-green-500 tw-mx-auto tw-mb-3" />
              <h5 className="tw-text-lg tw-font-semibold tw-mb-2">Votre commande a été enregistrée !</h5>
              <p className="tw-text-sm tw-text-gray-500 tw-mb-5">
                Vous recevrez une confirmation par email avec les détails de votre commande et le suivi de livraison.
              </p>
              <div className="tw-space-y-2">
                <button
                  className="tw-w-full tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-2.5 tw-text-sm tw-font-medium tw-rounded-lg tw-bg-affinicar-secondary tw-text-white hover:tw-opacity-90"
                  onClick={resetOrderStates}
                >
                  Voir mes commandes
                </button>
                <button
                  className="tw-w-full tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-border-gray-300 tw-text-gray-600 hover:tw-bg-gray-50"
                  onClick={() => { resetOrderStates(); onBackToCatalog(); }}
                >
                  <ArrowLeft className="tw-w-4 tw-h-4" />
                  Retour au catalogue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delivery form modal */}
      {showDeliveryForm && (
        <DeliveryForm
          defaultDeliveryData={deliveryData || defaultDeliveryData}
          onConfirm={handleDeliveryConfirm}
          onCancel={() => setShowDeliveryForm(false)}
        />
      )}
    </>
  );
}
